import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2 } from 'lucide-react'

import { ApiError } from '@/api/client'
import { teachersApi } from '@/api/endpoints'
import type { AvailabilitySlot, LocationBrief, Weekday } from '@/api/types'
import { Alert } from '@/components/ui/Alert'
import { Button, IconButton } from '@/components/ui/Button'
import { TimeSelect } from '@/components/ui/TimeSelect'
import { useToast } from '@/components/ui/Toast'
import { WEEKDAYS, WEEKDAY_LABELS, shortTime } from '@/lib/dictionaries'

interface Draft {
  weekday: Weekday
  start: string
  end: string
  locationId: string
}

interface Props {
  teacherId: string
  slots: AvailabilitySlot[]
  locations: LocationBrief[]
  canEdit: boolean
}

const CONTROL =
  'rounded-lg border-2 border-transparent bg-surface-container-low px-3 py-2 text-sm transition-colors focus:border-primary focus:bg-surface focus:outline-none'

/** Czy dwa przedziały czasu w tym samym dniu na siebie nachodzą. */
function overlaps(a: Draft, b: Draft): boolean {
  return a.weekday === b.weekday && a.start < b.end && b.start < a.end
}

export function AvailabilityEditor({ teacherId, slots, locations, canEdit }: Props) {
  const queryClient = useQueryClient()
  const { notifySuccess } = useToast()

  const [drafts, setDrafts] = useState<Draft[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setDrafts(
      slots.map((slot) => ({
        weekday: slot.weekday,
        start: shortTime(slot.start_time),
        end: shortTime(slot.end_time),
        locationId: slot.location_id ?? '',
      })),
    )
    setError(null)
  }, [slots])

  const mutation = useMutation({
    mutationFn: () =>
      teachersApi.replaceAvailability(
        teacherId,
        drafts.map((draft) => ({
          weekday: draft.weekday,
          // Backend oczekuje pełnego czasu z sekundami.
          start_time: `${draft.start}:00`,
          end_time: `${draft.end}:00`,
          location_id: draft.locationId || null,
          note: null,
        })),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['teachers', teacherId] })
      notifySuccess('Dostępność została zapisana.')
    },
    onError: (caught: unknown) => {
      setError(caught instanceof ApiError ? caught.message : 'Nie udało się zapisać dostępności.')
    },
  })

  const addRow = () => {
    setDrafts((current) => [
      ...current,
      { weekday: 'MONDAY', start: '14:00', end: '18:00', locationId: '' },
    ])
  }

  const updateRow = (index: number, patch: Partial<Draft>) => {
    setDrafts((current) =>
      current.map((draft, position) => (position === index ? { ...draft, ...patch } : draft)),
    )
  }

  const removeRow = (index: number) => {
    setDrafts((current) => current.filter((_, position) => position !== index))
  }

  // Walidacja lokalna daje natychmiastową informację zwrotną; backend i tak
  // sprawdza to samo przy zapisie.
  const invalidRange = drafts.some((draft) => draft.start >= draft.end)
  const hasOverlap = drafts.some((draft, index) =>
    drafts.slice(index + 1).some((other) => overlaps(draft, other)),
  )
  const canSave = canEdit && !invalidRange && !hasOverlap

  const grouped = WEEKDAYS.map((weekday) => ({
    weekday,
    rows: drafts
      .map((draft, index) => ({ draft, index }))
      .filter((entry) => entry.draft.weekday === weekday),
  })).filter((group) => group.rows.length > 0)

  return (
    <div className="space-y-5 p-6">
      <p className="text-sm leading-relaxed text-on-surface-variant">
        Okna dostępności podpowiadają wolne terminy przy układaniu grafiku i ostrzegają o lekcjach
        planowanych poza godzinami pracy.
      </p>

      {error && <Alert tone="error">{error}</Alert>}
      {invalidRange && (
        <Alert tone="warning">Godzina zakończenia musi być późniejsza niż rozpoczęcia.</Alert>
      )}
      {hasOverlap && <Alert tone="warning">Dwa okna w tym samym dniu nachodzą na siebie.</Alert>}

      {drafts.length === 0 ? (
        <p className="rounded-lg bg-surface-container-low p-4 text-sm text-on-surface-variant">
          Nie zadeklarowano jeszcze żadnych godzin.
        </p>
      ) : (
        <div className="space-y-5">
          {grouped.map((group) => (
            <div key={group.weekday}>
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-on-surface-variant">
                {WEEKDAY_LABELS[group.weekday]}
              </p>
              <ul className="space-y-2">
                {group.rows.map(({ draft, index }) => (
                  <li
                    key={index}
                    className="flex flex-wrap items-center gap-2 rounded-lg border border-outline/10 p-3"
                  >
                    <select
                      value={draft.weekday}
                      onChange={(event) =>
                        updateRow(index, { weekday: event.target.value as Weekday })
                      }
                      aria-label="Dzień tygodnia"
                      disabled={!canEdit}
                      className={CONTROL}
                    >
                      {WEEKDAYS.map((weekday) => (
                        <option key={weekday} value={weekday}>
                          {WEEKDAY_LABELS[weekday]}
                        </option>
                      ))}
                    </select>

                    <TimeSelect
                      label="Godzina rozpoczęcia"
                      labelHidden
                      value={draft.start}
                      onChange={(value) => updateRow(index, { start: value })}
                      disabled={!canEdit}
                      className="w-auto"
                    />
                    <span aria-hidden="true" className="text-on-surface-variant">
                      –
                    </span>
                    <TimeSelect
                      label="Godzina zakończenia"
                      labelHidden
                      value={draft.end}
                      onChange={(value) => updateRow(index, { end: value })}
                      disabled={!canEdit}
                      className="w-auto"
                    />

                    {locations.length > 1 && (
                      <select
                        value={draft.locationId}
                        onChange={(event) => updateRow(index, { locationId: event.target.value })}
                        aria-label="Oddział"
                        disabled={!canEdit}
                        className={CONTROL}
                      >
                        <option value="">Dowolny oddział</option>
                        {locations.map((location) => (
                          <option key={location.id} value={location.id}>
                            {location.name}
                          </option>
                        ))}
                      </select>
                    )}

                    {canEdit && (
                      <IconButton
                        label="Usuń okno dostępności"
                        icon={<Trash2 size={15} />}
                        onClick={() => removeRow(index)}
                        className="ml-auto hover:bg-error-container hover:text-error"
                      />
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {canEdit && (
        <div className="flex flex-wrap gap-3 border-t border-outline/10 pt-4">
          <Button variant="outline" size="sm" icon={<Plus size={16} />} onClick={addRow}>
            Dodaj okno
          </Button>
          <Button
            size="sm"
            isLoading={mutation.isPending}
            disabled={!canSave}
            onClick={() => mutation.mutate()}
          >
            Zapisz dostępność
          </Button>
        </div>
      )}
    </div>
  )
}
